import { useMemo } from 'react';
import { MetricConfig, CHART_PADDING } from '../config/chartConfig';
import { MetricTimeSeries, MetricDataPoint } from '@repo/shared/dtos/devices/SessionPpgDto';
import { SingleChart } from './SingleChart';

interface ChartSeries {
  id: string;
  metricKey: string;
  color: string;
  data: { x: number; y: number }[];
}

interface MultiAxisChartProps {
  selectedMetrics: string[];
  metricData: Record<string, MetricTimeSeries | null>;
  metricConfigs: Record<string, MetricConfig>;
  height?: number;
  minSlotPx?: number;
  startTime?: string;
}

function buildChartSeries(
  data: MetricDataPoint[],
  epochOrigin: number,
): { x: number; y: number }[] {
  return data.map(p => ({
    x: (new Date(p.timestamp).getTime() - epochOrigin) / 1000,
    y: p.value,
  }));
}

function buildSeries(
  metrics: string[],
  metricData: Record<string, MetricTimeSeries | null>,
  metricConfigs: Record<string, MetricConfig>,
  startTime?: string,
): ChartSeries[] {
  return metrics
    .map(m => {
      const data = metricData[m];
      const config = metricConfigs[m];
      if (!data?.data.length || !config) return null;

      const epochOrigin = startTime
        ? new Date(startTime).getTime()
        : new Date(data.data[0].timestamp).getTime();

      return {
        id: config.id,
        metricKey: m,
        color: config.color,
        data: buildChartSeries(data.data, epochOrigin),
      };
    })
    .filter((s): s is ChartSeries => s !== null);
}

function calcTimeRange(allSeries: ChartSeries[]) {
  const xs = allSeries.flatMap(s => s.data.map(p => p.x));
  if (xs.length === 0) return null;
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const padding = (maxX - minX) * CHART_PADDING.TIME_AXIS;
  return { start: Math.max(0, minX - padding), end: maxX + padding };
}

export const MultiAxisChart = ({
  selectedMetrics,
  metricData,
  metricConfigs,
  height = 400,
  minSlotPx = 150,
  startTime,
}: MultiAxisChartProps) => {
  const allSeries = useMemo(
    () => buildSeries(selectedMetrics, metricData, metricConfigs, startTime),
    [selectedMetrics, metricData, metricConfigs, startTime],
  );

  const timeRange = useMemo(() => calcTimeRange(allSeries), [allSeries]);

  if (allSeries.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border-2 border-dashed border-[var(--anl-border)] bg-[var(--anl-surface)]"
        style={{ height }}
      >
        <p className="text-sm text-[var(--anl-text-muted)]">No data available for selected metrics</p>
      </div>
    );
  }

  const slotHeight = Math.max(minSlotPx, Math.floor(height / allSeries.length));
  const wrapperHeight = slotHeight * allSeries.length;

  return (
    <div
      style={{ height: wrapperHeight }}
      className="overflow-hidden rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)]"
    >
      <div className="flex flex-col" style={{ height: wrapperHeight }}>
        {allSeries.map((series, idx) => (
          <div key={series.id} style={{ height: slotHeight }}>
            <SingleChart
              series={series}
              config={metricConfigs[series.metricKey]}
              height={slotHeight}
              timeRange={timeRange}
              isLast={idx === allSeries.length - 1}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
